import React, { useState } from "react";
import { AdminMerchant } from "../../../../interface/interfaces";

interface MerchantDetailsModalProps {
  merchant: AdminMerchant;
  isOpen: boolean;
  onClose: () => void;
}

const MerchantDetailsModal: React.FC<MerchantDetailsModalProps> = ({
  merchant,
  isOpen,
  onClose,
}) => {
  const [expandedFields, setExpandedFields] = useState<{
    merchantRef: boolean;
    settlementAccount: boolean;
  }>({
    merchantRef: false,
    settlementAccount: false,
  });

  const [copiedField, setCopiedField] = useState<string | null>(null);

  if (!isOpen) return null;

  const truncateText = (
    text: string | null,
    maxLength: number = 20
  ): string => {
    if (!text) return "Not Available";
    return text.length > maxLength
      ? `${text.substring(0, maxLength)}...`
      : text;
  };

  const copyToClipboard = async (text: string, fieldName: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(fieldName);
      setTimeout(() => setCopiedField(null), 2000);
    } catch (err) {
      console.error("Failed to copy text: ", err);
    }
  };

  const toggleExpand = (field: "merchantRef" | "settlementAccount") => {
    setExpandedFields((prev) => ({
      ...prev,
      [field]: !prev[field],
    }));
  };

  const renderExpandableField = (
    text: string | null,
    fieldKey: "merchantRef" | "settlementAccount",
    maxLength: number,
    copyLabel: string
  ) => {
    if (!text) {
      return <p className="text-gray-500 italic">Not Available</p>;
    }

    const isExpanded = expandedFields[fieldKey];
    const needsTruncation = text.length > maxLength;
    const displayText =
      isExpanded || !needsTruncation ? text : truncateText(text, maxLength);

    return (
      <div className="space-y-2">
        <div className="flex items-start gap-2">
          <p className="text-gray-900 font-mono text-sm bg-gray-50 px-2 py-1 rounded border flex-1 break-all">
            {displayText}
          </p>
          <div className="flex gap-1">
            <button
              onClick={() => copyToClipboard(text, fieldKey)}
              className={`text-xs px-2 py-1 rounded transition-colors duration-200 ${
                copiedField === fieldKey
                  ? "bg-green-100 text-green-700"
                  : "text-blue-500 hover:text-blue-700 hover:bg-blue-50"
              }`}
              title={copyLabel}
            >
              {copiedField === fieldKey ? (
                <div className="flex items-center gap-1">
                  <svg
                    className="w-3 h-3"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                  <span>Copied!</span>
                </div>
              ) : (
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                  />
                </svg>
              )}
            </button>
          </div>
        </div>
        {needsTruncation && (
          <button
            onClick={() => toggleExpand(fieldKey)}
            className="text-xs text-blue-600 hover:text-blue-800 font-medium flex items-center gap-1"
          >
            {isExpanded ? (
              <>
                <svg
                  className="w-3 h-3"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 15l7-7 7 7"
                  />
                </svg>
                Show Less
              </>
            ) : (
              <>
                <svg
                  className="w-3 h-3"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
                Show Full {fieldKey === "merchantRef" ? "Reference" : "Account"}
              </>
            )}
          </button>
        )}
      </div>
    );
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b px-6 py-4 flex justify-between items-center">
          <h3 className="text-xl font-semibold text-gray-900">
            Merchant Details - {merchant.name}
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 p-1 hover:bg-gray-100 rounded"
          >
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* Basic Info */}
            <div className="space-y-4">
              <h4 className="font-semibold text-gray-900 border-b pb-2">
                Basic Information
              </h4>
              <div>
                <label className="text-sm font-medium text-gray-500">
                  Name
                </label>
                <p className="text-gray-900 font-medium">{merchant.name}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">
                  Email
                </label>
                <p className="text-gray-900 break-all">{merchant.email}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">
                  Phone Number
                </label>
                <p className="text-gray-900">{merchant.phoneNumber}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">
                  Code
                </label>
                <p className="text-gray-900 font-mono bg-gray-100 px-2 py-1 rounded inline-block">
                  {merchant.code}
                </p>
              </div>
            </div>

            {/* Financial Info */}
            <div className="space-y-4">
              <h4 className="font-semibold text-gray-900 border-b pb-2">
                Financial Information
              </h4>
              <div>
                <label className="text-sm font-medium text-gray-500">
                  Status
                </label>
                <p>
                  <span
                    className={`px-2 py-1 rounded-full text-xs font-medium ${
                      merchant.status === "ACTIVE"
                        ? "bg-green-100 text-green-800"
                        : "bg-red-100 text-red-800"
                    }`}
                  >
                    {merchant.status}
                  </span>
                </p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">
                  Transaction Fee
                </label>
                <p className="text-gray-900 font-semibold">
                  {(merchant.transactionFee * 100).toFixed(2)}%
                </p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">
                  Merchant Reference
                </label>
                {renderExpandableField(
                  merchant.merchantRef,
                  "merchantRef",
                  25,
                  "Copy full reference"
                )}
              </div>
            </div>

            {/* Banking Info */}
            <div className="space-y-4">
              <h4 className="font-semibold text-gray-900 border-b pb-2">
                Banking Information
              </h4>
              <div>
                <label className="text-sm font-medium text-gray-500">
                  Bank Name
                </label>
                <p className="text-gray-900">
                  {merchant.bankName || (
                    <span className="text-gray-500 italic">Not Available</span>
                  )}
                </p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">
                  Bank Institution Code
                </label>
                <p className="text-gray-900">
                  {merchant.bankInstitutionCode || (
                    <span className="text-gray-500 italic">Not Available</span>
                  )}
                </p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">
                  Settlement Account
                </label>
                {renderExpandableField(
                  merchant.settlementAccount,
                  "settlementAccount",
                  15,
                  "Copy full account number"
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="sticky bottom-0 bg-gray-50 px-6 py-4 flex justify-end border-t">
          <button
            onClick={onClose}
            className="bg-gray-500 hover:bg-gray-600 text-white px-6 py-2 rounded transition-colors duration-200"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default MerchantDetailsModal;
